import { Download } from "lucide-react";
import { useGetOne, useRecordContext, useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";
import type { Contact, Company } from "../types";
import { downloadVCard, type VCardPhoto } from "./vcard";

export const ExportVCardButton = () => {
  const contact = useRecordContext<Contact>();
  const translate = useTranslate();

  // Fetch the company data on mount
  const { data: company } = useGetOne<Company>(
    "companies",
    { id: contact?.company_id ?? undefined },
    { enabled: !!contact?.company_id },
  );

  const handleExport = async () => {
    if (!contact) return;

    // Fetch and convert avatar to base64 if it exists
    let photoData: VCardPhoto | undefined = undefined;

    if (contact.avatar?.src) {
      try {
        const response = await fetch(contact.avatar.src);
        const blob = await response.blob();
        const mimeType = blob.type || "image/png";

        // Convert blob to base64
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const result = reader.result as string;
            // Remove the data URL prefix (data:image/png;base64,)
            const base64Data = result.split(",")[1];
            resolve(base64Data);
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        photoData = { base64, mimeType };
      } catch (error) {
        console.error("Failed to fetch avatar image:", error);
        // Continue without the photo
      }
    }

    downloadVCard(contact, company?.name, photoData);
  };

  if (!contact) return null;

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleExport}
      className="h-6 cursor-pointer"
    >
      <Download className="w-4 h-4" />
      {translate("resources.contacts.action.export_vcard", {
        _: "Export to vCard",
      })}
    </Button>
  );
};
